import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import {
  AdminBlogShell,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { LoadingSpinner } from '../../../shared/ui';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import {
  buildYowanPersonOptions,
  fetchYowanAdminPersonList,
  getYowanAdminMutationErrorMessage,
  inviteYowanAdminBlogAuthorAccess,
  resolveYowanAdminPersonId,
  updateYowanAdminBlogAuthorAccess,
} from '../app/yowanContentClient';
import AdminPersonLedgerSection from './AdminPersonLedgerSection';
const { yowanPageContent } = require('./pageContent');

const hasPersonLedgerInput = (values = {}) => [
  values.personDisplayName,
  values.personNameKana,
  values.personEmail,
  values.personPhone,
  values.personOrganization,
  values.personRoleTitle,
  values.personInternalNote,
].some((value) => String(value || '').trim());

export const AdminBlogAuthorEditorFields = ({
  content,
  register,
  errors,
  emailReadOnly = false,
  showSendInvite = false,
}) => (
  <>
    <label className="c-form__field">
      <span className="c-form__label">
        {content.form.labels.email}
        <em className="c-form__required">*</em>
      </span>
      <input
        className="c-form__control"
        type="email"
        readOnly={emailReadOnly}
        style={emailReadOnly ? { cursor: 'not-allowed' } : undefined}
        {...register('email', { required: content.form.errors.required })}
      />
      {errors?.email ? <em className="yowan-admin-form__error">{errors.email.message}</em> : null}
      {content.form.emailHelpText ? (
        <p className="yowan-admin-form__field-note yowan-admin-form__field-note--compact">
          {content.form.emailHelpText}
        </p>
      ) : null}
    </label>

    <label className="c-form__field">
      <span className="c-form__label">
        {content.form.labels.authorName}
        <em className="c-form__required">*</em>
      </span>
      <input
        className="c-form__control"
        type="text"
        {...register('authorName', { required: content.form.errors.required })}
      />
      {errors?.authorName ? <em className="yowan-admin-form__error">{errors.authorName.message}</em> : null}
    </label>

    <label className="c-form__field">
      <span className="c-form__label">{content.form.labels.profileUrl}</span>
      <input
        className="c-form__control"
        type="url"
        {...register('profileUrl', {
          pattern: {
            value: /^https:\/\/.+/i,
            message: content.form.errors.invalidUrl,
          },
        })}
      />
      {errors?.profileUrl ? <em className="yowan-admin-form__error">{errors.profileUrl.message}</em> : null}
      {content.form.profileUrlHelpText ? (
        <p className="yowan-admin-form__field-note yowan-admin-form__field-note--compact">
          {content.form.profileUrlHelpText}
        </p>
      ) : null}
    </label>

    <label className="c-form__field c-form__field--checkbox">
      <span className="c-form__label">{content.form.labels.canPublishBlog}</span>
      <input type="checkbox" {...register('canPublishBlog')} />
      {content.form.canPublishBlogHelpText ? (
        <p className="yowan-admin-form__field-note yowan-admin-form__field-note--compact">
          {content.form.canPublishBlogHelpText}
        </p>
      ) : null}
    </label>

    <label className="c-form__field c-form__field--checkbox">
      <span className="c-form__label">{content.form.labels.isActive}</span>
      <input type="checkbox" {...register('isActive')} />
    </label>

    {showSendInvite ? (
      <label className="c-form__field c-form__field--checkbox">
        <span className="c-form__label">{content.form.labels.sendInvite}</span>
        <input type="checkbox" {...register('sendInvite')} />
        {content.form.sendInviteHelpText ? (
          <p className="yowan-admin-form__field-note yowan-admin-form__field-note--compact">
            {content.form.sendInviteHelpText}
          </p>
        ) : null}
      </label>
    ) : null}
  </>
);

const AdminBlogAuthorInvitePage = () => {
  const content = yowanPageContent['/admin/content/blog-authors/new'];
  const navigate = useNavigate();
  const preservedDomainSearch = usePreservedDomainSearch();
  const [personOptions, setPersonOptions] = useState([]);
  const [isLoadingPersonOptions, setIsLoadingPersonOptions] = useState(false);
  const [personOptionLoadError, setPersonOptionLoadError] = useState('');
  const [submitMessage, setSubmitMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: content?.form.defaults,
  });

  useEffect(() => {
    if (!content) {
      return undefined;
    }

    let isMounted = true;

    const loadPersonOptions = async () => {
      setIsLoadingPersonOptions(true);
      setPersonOptionLoadError('');

      try {
        const people = await fetchYowanAdminPersonList();

        if (isMounted) {
          setPersonOptions(buildYowanPersonOptions(people.results));
        }
      } catch (error) {
        if (isMounted) {
          setPersonOptionLoadError('人物台帳の選択肢を読み込めませんでした。');
        }
      } finally {
        if (isMounted) {
          setIsLoadingPersonOptions(false);
        }
      }
    };

    loadPersonOptions();

    return () => {
      isMounted = false;
    };
  }, [content]);

  if (!content) {
    return null;
  }

  const onSubmit = async (values) => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const selectedPersonId = values.personId || '';
      const shouldResolvePerson = Boolean(selectedPersonId || hasPersonLedgerInput(values));
      const personId = shouldResolvePerson
        ? await resolveYowanAdminPersonId({
            ...values,
            personId: selectedPersonId,
          })
        : null;

      const createdAccess = await inviteYowanAdminBlogAuthorAccess({ ...values, personId });

      if (personId && createdAccess?.id && personId !== createdAccess.personId) {
        await updateYowanAdminBlogAuthorAccess(createdAccess.id, { ...values, personId });
      }

      navigate(appendYowanPreviewQueryIfNeeded('/admin/content/blog-authors', preservedDomainSearch));
    } catch (error) {
      setSubmitMessage(getYowanAdminMutationErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminBlogShell activeNavHref="/admin/content/blog-authors" routeKey="/admin/content/blog-authors/new">
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="back-to-list" to={appendYowanPreviewQueryIfNeeded('/admin/content/blog-authors', preservedDomainSearch)}>
            一覧へ戻る
          </Link>,
        ]}
      />

      <form className="yowan-admin-form c-surface-content" onSubmit={handleSubmit(onSubmit)}>
        <AdminBlogAuthorEditorFields
          content={content}
          register={register}
          errors={errors}
          showSendInvite
        />
        <AdminPersonLedgerSection
          content={content}
          register={register}
          errors={errors}
          watch={watch}
          setValue={setValue}
          personOptions={personOptions}
          isLoadingOptions={isLoadingPersonOptions}
          optionLoadError={personOptionLoadError}
        />
        <div className="yowan-admin-form__actions">
          <button type="submit" className="c-form__submit" disabled={isSubmitting}>
            {content.form.submitLabel}
          </button>
        </div>
        {isSubmitting ? (
          <p className="yowan-admin-form__message yowan-admin-form__message--pending">
            <LoadingSpinner inline size="sm" />
            <span>{content.form.pendingMessage}</span>
          </p>
        ) : submitMessage ? (
          <p className="yowan-admin-form__message">{submitMessage}</p>
        ) : null}
      </form>
    </AdminBlogShell>
  );
};

export default AdminBlogAuthorInvitePage;
